/**
 * @file 应用初始化引导模块
 * @description 提供统一的应用初始化逻辑，通过平台策略模式委托各平台特有初始化
 */

import qs from 'qs'
import {
  platformRegistry,
  detectPlatform,
  applyOverrides,
  type StrategyOverride
} from '@h5-journey/platform'
import { useUserStore, useAppStore } from '@h5-journey/stores'
import { captureChannelSource, trackAppEntry, setTrackSource } from '@h5-journey/track'
import type { Router } from 'vue-router'

/** 初始化配置 */
export interface BootstrapOptions {
  /** 路由实例 */
  router: Router
  /** 默认终端编码，30：新旅行APP；04：微信小程序 ；41：好时光工行；42：新旅行H5 */
  defaultTerminalCode?: string
  /** 默认渠道ID */
  defaultChannelId?: string
  /** 默认渠道标识，对应NBS渠道。新旅行APP与小程序渠道号是一样的 */
  defaultSourceFlag?: string
  /** 登录成功弹窗回调 */
  onLoginSuccessModal?: (data: LoginSuccessModalData) => void
  /** 埋点来源标识（各应用唯一，如 h5_member），用于区分应用维度；不传默认 web */
  trackSource?: string
  /**
   * 平台策略覆盖项数组
   * - 默认不传，行为不变
   * - 每项可指定 platform 条件，仅匹配当前平台时生效
   * - 项内按域（navigation/communication/location）浅合并，只需提供要覆盖的方法
   */
  override?: StrategyOverride[]
}

/** 登录成功弹窗数据 */
export interface LoginSuccessModalData {
  newUserFlag: boolean
  userSource: string
  picUrl: string
}

/**
 * 初始化默认 storage 值
 * 优先级：URL query > 已有 sessionStorage > defaultXxx 兜底
 */
function initDefaultStorage(options: BootstrapOptions): void {
  const {
    defaultTerminalCode = '30',
    defaultChannelId = '153',
    defaultSourceFlag = '14|75|76'
  } = options

  // URL 优先：解析 URL 的 channel 参数并覆盖写入 channelId，避免依赖业务页面单独处理
  const search = window.location.search.replace('?', '')
  const urlParams = qs.parse(search) as { channel?: unknown }
  const urlChannel =
    typeof urlParams.channel === 'string' ? urlParams.channel.trim().replace(/_/g, '|') : ''
  if (urlChannel) {
    sessionStorage.setItem('sourceFlag', urlChannel)
  }

  // 仅在没有值时设置默认值
  if (!sessionStorage.getItem('terminalCode')) {
    sessionStorage.setItem('terminalCode', defaultTerminalCode)
  }
  if (!sessionStorage.getItem('channelId')) {
    sessionStorage.setItem('channelId', defaultChannelId)
  }
  if (!sessionStorage.getItem('sourceFlag')) {
    sessionStorage.setItem('sourceFlag', defaultSourceFlag)
  }

  // 同步到 AppStore
  const appStore = useAppStore()
  appStore.terminalCode = sessionStorage.getItem('terminalCode') || defaultTerminalCode
  appStore.channelId = sessionStorage.getItem('channelId') || defaultChannelId
  appStore.sourceFlag = sessionStorage.getItem('sourceFlag') || defaultSourceFlag
}

/**
 * 开发环境调试信息注入
 * 仅在 VITE_APP_ENV=development 时生效
 * 调试信息通过 .env.development.local 文件配置（已在 .gitignore 中忽略）
 */
function injectDevDebugInfo(userStore: ReturnType<typeof useUserStore>): void {
  const appEnv = import.meta.env.VITE_APP_ENV

  if (appEnv !== 'development') {
    return
  }

  const mobile = import.meta.env.VITE_DEBUG_MOBILE
  const loginToken = import.meta.env.VITE_DEBUG_LOGIN_TOKEN
  const loginId = import.meta.env.VITE_DEBUG_LOGIN_ID
  const memberId = import.meta.env.VITE_DEBUG_MEMBER_ID

  if (mobile || loginToken || loginId || memberId) {
    userStore.setUserInfo({
      mobile: mobile || '',
      loginToken: loginToken || '',
      loginId: loginId || '',
      memberId: memberId || ''
    })
    console.log('[DEV] 已注入调试用户信息')
  }
}

/**
 * 应用初始化引导
 * @param options - 初始化配置
 * @returns Promise<void>
 *
 * @example
 * ```ts
 * import { bootstrap } from '@h5-journey/bootstrap'
 * import { router } from './router'
 *
 * const app = createApp(App)
 * app.use(createPinia()).use(router)
 *
 * bootstrap({
 *   router,
 *   onLoginSuccessModal: (data) => {
 *     // 处理登录成功弹窗
 *   }
 * }).then(() => {
 *   app.mount('#app')
 * })
 * ```
 */
export async function bootstrap(options: BootstrapOptions): Promise<void> {
  const { router } = options
  const userStore = useUserStore()

  // 1. 检测并注册平台策略
  const strategy = detectPlatform()
  platformRegistry.register(strategy)

  // 2. 应用平台策略覆盖（在注册之后、业务初始化之前合并到策略实例）
  applyOverrides(strategy, options.override, strategy.type)

  // 3. 公共初始化（所有平台共享）
  initDefaultStorage(options)
  injectDevDebugInfo(userStore)

  // 3.1 渠道来源采集与"进入应用"埋点
  // 须在业务请求前完成采集，使后续埋点/接口也能带上渠道维度；进入事件仅自研 /stat/mark 上报
  if (options.trackSource) {
    setTrackSource(options.trackSource)
  }
  captureChannelSource()
  trackAppEntry()

  console.log('=== 开始应用初始化 ===')

  // 4. 并行执行：路由就绪 + 平台特有初始化（含 Request-From 处理）
  await Promise.all([router.isReady(), strategy.onInit({ userStore, options })])

  console.log(`[Bootstrap] 平台=${strategy.type} 初始化完成`)
}

/**
 * 创建简化的初始化函数（适用于不需要完整配置的场景）
 */
export function createBootstrap(defaultOptions: Partial<BootstrapOptions> = {}) {
  return (options: BootstrapOptions) => bootstrap({ ...defaultOptions, ...options })
}

// re-export：方便应用层从 bootstrap 包直接导入覆盖类型
export type { StrategyOverride } from '@h5-journey/platform'
